import {
  nonReactSetCurrentTime,
  useRealTimeStore,
} from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { Store } from '../../../store/player/types';
import { setCurrentIframeContent } from './setCurrentIframeContent';
import { onHandleEvents } from './onHandleEvents';
import { maybeLogDatadogRum } from './maybeLogDatadogRum';
import { pause } from './pause';
import { handleViewportResizeEvent } from './handleViewportResizeEvent';

import { finishRecording } from './finishRecording';

export const onPlayTick = (store: Store): void => {
  const {
    currentPageIndex,
    playTickInterval,
    playTimeoutId,
    pageToStartProcess,
    skipNonMutationEvents,
    playbackScript,
    processedPosition,
    currentPosition,
    targetEndTime,
    jumpToPause,
    recording,
    skipPauses,
    playbackSpeed,
    currentPageOriginalUrl,
    startPageContentIdentifier,
    jumpToCallback,
    endCallback,
    playing,

    setPlayTimeoutId,
    setPageToStartProcess,
    setCurrentPageIndex,
    setPosition,
    setJumpToPause,
    setSkipNonMutationEvents,
    setShowLoader,
    setJumpToCallback,
  } = store.getState();

  if (!recording) return;
  if (!playing && !jumpToPause) return;

  const page = recording.page_visit_recordings[currentPageIndex];
  const pageEvents = page.events;
  let playTickDelay = playTickInterval / playbackSpeed;

  // Initial start of playback, load the first page and wait until it's done.
  if (currentPageOriginalUrl === null) {
    setCurrentIframeContent(store);

    setPlayTimeoutId(+setTimeout(() => onPlayTick(store), playTickDelay));
    return;
  }

  // If the page hasn't yet loaded, do not continue.
  if (!window.playbackModuleHasLoadedThePage) {
    setPlayTimeoutId(+setTimeout(() => onPlayTick(store), playTickDelay));
    return;
  }

  setShowLoader(false);

  // If we have a page to start processing from, we first execute those events.
  if (pageToStartProcess >= 0) {
    const currentPage = recording.page_visit_recordings[currentPageIndex];

    // We go through all the events in all the pages between the one we're starting at and the
    // one the user is in. We also ensure we skip pages that do not match the same page content
    // identifier as the one the user is in.
    for (let i = pageToStartProcess; i < currentPageIndex; i++) {
      const page = recording.page_visit_recordings[i];
      if (page.start_page_content_identifier !== currentPage.start_page_content_identifier) {
        continue;
      }

      const pageToProcess = recording.page_visit_recordings[i];
      onHandleEvents(store, i, 0, pageToProcess.events.length - 1, skipNonMutationEvents);
    }
    playbackScript.clearTrail();
  }
  setPageToStartProcess(-1);

  maybeLogDatadogRum(store);
  const newTime = useRealTimeStore.getState().currentTime + playTickInterval;
  // Prevent instances where the current time can be larger than the total time.
  nonReactSetCurrentTime(Math.min(newTime, recording.duration));

  // Check if we are in a user inactive section to advance the time
  // Note: It doesn't mean we have no event to handle just that there is no user actions in it
  if (skipPauses) {
    const realTimeStoreState = useRealTimeStore.getState();
    const currentSection =
      realTimeStoreState.sectionInfoList[realTimeStoreState.currentSectionIndex];
    if (currentSection && !currentSection.isActive) {
      const targetTime = currentSection.startTimeInMs + currentSection.durationInMs + 1;
      maybeLogDatadogRum(store);
      nonReactSetCurrentTime(Math.min(targetTime, realTimeStoreState.duration));
    }
  }

  let hasEvents = false;
  let newPosition = currentPosition;
  // Fetch all events that we should process on this tick.
  for (let i = processedPosition; i < pageEvents.length; i++) {
    if (pageEvents[i].time_in_timeline <= useRealTimeStore.getState().currentTime) {
      hasEvents = true;
      newPosition = i;
    } else {
      // If not, we're going to play out this interval like any other.
      // Either way, we break to let a play tick happen as normal.
      break;
    }
  }

  // Let the callback handle the events and update the UI.
  if (hasEvents) {
    onHandleEvents(store, currentPageIndex, processedPosition, newPosition, skipNonMutationEvents);
    setPosition(newPosition, newPosition + 1);
  } else {
    playbackScript.inactiveTick(useRealTimeStore.getState().currentTime);
    if (skipPauses) {
      // Non-event time is set to 0ms so we can skip it later - cancelling the timeout.
      playTickDelay = 0;
    }
  }

  // If we have a targetEndTime set we need to stop and try to call the callback.
  if (targetEndTime && useRealTimeStore.getState().currentTime >= targetEndTime && endCallback) {
    endCallback();
  }

  // If jumpToPause is set, pause the recording (now that we've reloaded the events) and set it to
  // false.
  if (jumpToPause) {
    pause(store);
    setJumpToPause(false);
  } else if (
    page.time_in_timeline + page.duration <= newTime &&
    pageEvents.length - 1 <= currentPosition
  ) {
    if (currentPageIndex < recording.page_visit_recordings.length - 1) {
      // This page is done, but there are still more pages.

      // Increase current page.
      const newPageIndex = currentPageIndex + 1;
      setCurrentPageIndex(newPageIndex);

      // Set the currentPosition to a negative number to avoid flagging single-event pages as
      // done and reset the events taht we have processed.
      setPosition(-1, 0);

      const newPageContentIdentifier =
        recording.page_visit_recordings[newPageIndex].start_page_content_identifier;

      // Similar to the logic of the jumpTo method, if we move to a page with a different
      // page content identifier, we need to find the first page in the list with that same
      // identifier so that we can replay all events up until that new page.
      if (startPageContentIdentifier !== newPageContentIdentifier) {
        let newPageToStartProcess = newPageIndex;

        for (let i = newPageIndex; i >= 0; i--) {
          const pvr = recording.page_visit_recordings[i];
          if (pvr.start_page_content_identifier !== newPageContentIdentifier) {
            continue;
          }

          newPageToStartProcess = i;
        }

        setPageToStartProcess(newPageToStartProcess);
        setCurrentIframeContent(store, newPageToStartProcess);
      } else {
        setCurrentIframeContent(store);
      }

      // Make sure that any change in viewport dimensions is caught (HJ-7316).
      handleViewportResizeEvent(store, {
        w: recording.page_visit_recordings[newPageIndex].window_width,
        h: recording.page_visit_recordings[newPageIndex].window_height,
      });

      if (!playTickDelay) {
        clearTimeout(playTimeoutId as number);
        onPlayTick(store);
      } else {
        setPlayTimeoutId(+setTimeout(() => onPlayTick(store), playTickDelay));
      }
    } else {
      // There are no more pages, recording has ended.
      // If we have an endCallback we call that instead of doing the usual finishing off.
      if (endCallback) {
        endCallback();
      } else {
        finishRecording(store);
      }
    }
  } else {
    if (!playTickDelay) {
      clearTimeout(playTimeoutId as number);
      setTimeout(() => {
        onPlayTick(store);
      }, 0);
    } else {
      setPlayTimeoutId(+setTimeout(() => onPlayTick(store), playTickDelay));
    }
  }

  // If we arrive here and have a jumpToCallback then we know that the page has loaded and we can call
  // and remove the callback (it's always a single-use).
  if (jumpToCallback) {
    jumpToCallback();
    setJumpToCallback(null);
  }

  setSkipNonMutationEvents(false);
};
